import React, { useState, useRef } from "react";
import * as PropTypes from "prop-types";
import {
  Box,
  Card,
  CardActions,
  CardContent,
  Typography,
  Button,
  Chip,
  Popper,
  Paper,
  ClickAwayListener,
  MenuList,
  MenuItem,
  useTheme,
  Grid,
  IconButton,
} from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import _ from "lodash";
import { useSnackbar } from "notistack";
import clipboardy from "clipboardy";
import numeral from "numeral";

import { formatDate } from "utils";
import { isEdgeV2 } from "utils/compType";

const productSkuCategoryColorMap = {
  "EdgeIPS Family": {
    background: "#1274FBB3",
    border: "#1274FB",
  },
  "EdgeFire Family": {
    background: "#16B1B9",
    border: "#16B1B9",
  },
  CMS: {
    background: "#984EFBB3",
    border: "#984EFB",
  },
};

const ComponentCard = ({ compInfo }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const moreEleRef = useRef();
  const [openMenu, setOpenMenu] = useState(false);
  const handleCloseMenu = (e) => {
    if (moreEleRef.current && moreEleRef.current.contains(e.targe)) {
      return;
    }
    setOpenMenu(false);
  };

  return (
    <Card
      sx={{
        margin: theme.spacing(1),
        padding: theme.spacing(0.5),
      }}
      variant="outlined"
      color="primary"
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: theme.spacing(0.5),
          }}
        >
          <Box
            className="123"
            sx={{
              display: "flex",
              alignItems: "center",
              "& > *:not(div:first-of-type)": {
                marginLeft: theme.spacing(1),
              },
            }}
          >
            {_.get(compInfo, "productSkuList", []).map((skuInfo) => (
              <Chip
                key={skuInfo.productSku}
                label={skuInfo.productSku}
                size="small"
                style={{
                  backgroundColor: _.get(
                    productSkuCategoryColorMap,
                    `${skuInfo.productCategory}.background`,
                    "#d1d1d1"
                  ),
                  border: `1px solid ${_.get(
                    productSkuCategoryColorMap,
                    `${skuInfo.productCategory}.border`,
                    "#bababa"
                  )}`,
                  color: "#FFFFFF",
                }}
              />
            ))}
            {isEdgeV2(_.get(compInfo, "compId"), _.get(compInfo, "version")) ? (
              <Chip
                label="Edge V2"
                size="small"
                style={{
                  backgroundColor: "#FC5488",
                  border: "#ba3d65",
                  color: "#FFFFFF",
                }}
              />
            ) : null}
          </Box>
          <IconButton
            title="more"
            ref={moreEleRef}
            onClick={() => setOpenMenu((prevState) => !prevState)}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
        <Typography variant="overline" gutterBottom>
          {formatDate(compInfo.releasedAt)}
        </Typography>
        <Typography
          variant="h5"
          component="div"
          gutterBottom
          sx={{
            fontWeight: 800,
            marginBottom: 0,
          }}
        >
          {compInfo.compName}
        </Typography>
        <Typography
          variant="body2"
          gutterBottom
          sx={{
            marginBottom: theme.spacing(2),
          }}
        >
          {compInfo.version}
        </Typography>

        {_.get(compInfo, "modelInfo", "").length ? (
          <Grid container>
            <Grid item xs={3}>
              <Typography
                variant="body2"
                component="p"
                color="textSecondary"
                sx={{
                  fontSize: 12,
                  lineBreak: "anywhere",
                }}
              >
                Model Info:
              </Typography>
            </Grid>
            <Grid item xs={9}>
              <Typography
                variant="body2"
                component="p"
                color="textSecondary"
                sx={{
                  fontSize: 12,
                  lineBreak: "anywhere",
                }}
              >
                {_.get(compInfo, "modelInfo")}
              </Typography>
            </Grid>
          </Grid>
        ) : null}
        <Grid container>
          <Grid item xs={3}>
            <Typography
              variant="body2"
              component="p"
              color="textSecondary"
              sx={{
                fontSize: 12,
                lineBreak: "anywhere",
              }}
            >
              File Size:
            </Typography>
          </Grid>
          <Grid item xs={9}>
            <Typography
              variant="body2"
              component="p"
              color="textSecondary"
              sx={{
                fontSize: 12,
                lineBreak: "anywhere",
              }}
            >
              {numeral(_.get(compInfo, "fileSize", 0)).format("0b")}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
      <CardActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: theme.spacing(2),
          paddingTop: theme.spacing(1),
        }}
      >
        <Button
          size="small"
          variant="contained"
          color="secondary"
          startIcon={<GetAppIcon />}
          sx={{
            padding: "8px 15px",
          }}
          target="_blank"
          href={`/api/v1/file/download/${compInfo.compId}/${
            compInfo.version
          }?t=${new Date().getTime()}`}
        >
          Download
        </Button>
      </CardActions>

      <Popper open={openMenu} anchorEl={moreEleRef.current}>
        <Paper>
          <ClickAwayListener onClickAway={handleCloseMenu}>
            <MenuList>
              <MenuItem
                onClick={(e) => {
                  try {
                    clipboardy.write(
                      _.get(compInfo, "sha256", "").toUpperCase()
                    );
                    enqueueSnackbar("File checksum copied", {
                      variant: "success",
                    });
                  } catch (err) {
                    console.err(err);
                    enqueueSnackbar(
                      "Failed to copy file checksum into clipboard",
                      {
                        variant: "error",
                      }
                    );
                  }
                  handleCloseMenu(e);
                }}
              >
                Copy SHA256 Checksum
              </MenuItem>
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popper>
    </Card>
  );
};
ComponentCard.propTypes = {
  compInfo: PropTypes.object.isRequired,
};
export default ComponentCard;
